import {
	SavedSearchQueryService,
	type RoleReference,
} from '@supply-mover-2-0/core';
import { useCallback, useEffect, useState } from 'react';

interface UseShareableRolesReturn {
	roles: Array<RoleReference>;
	canShareWithAny: boolean;
	isLoading: boolean;
	error: Error | null;
	refresh: () => Promise<void>;
}

/**
 * Hook to fetch roles the current user can share saved searches with.
 * If user has role management permission, returns all roles.
 * Otherwise, returns only roles they are a member of.
 */
export function useShareableRoles(): UseShareableRolesReturn {
	const [roles, setRoles] = useState<Array<RoleReference>>([]);
	const [canShareWithAny, setCanShareWithAny] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);

	const fetchRoles = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const result = await SavedSearchQueryService.getShareableRoles();
			if (result.success) {
				setRoles(result.data.roles);
				setCanShareWithAny(result.data.canShareWithAny);
			} else {
				setError(new Error(result.error ?? 'Failed to load roles'));
			}
		} catch (err) {
			setError(err instanceof Error ? err : new Error('Failed to load roles'));
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchRoles();
	}, [fetchRoles]);

	return {
		roles,
		canShareWithAny,
		isLoading,
		error,
		refresh: fetchRoles,
	};
}
